import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import type { ActivityStep } from '@/types/activity';

interface SortableStepItemProps {
  id: string;
  step: ActivityStep;
  index: number;
  onUpdate: (index: number, field: 'text' | 'emphasis', value: string) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

export function SortableStepItem({
  id,
  step,
  index,
  onUpdate,
  onRemove,
  canRemove,
}: SortableStepItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-4">
      <div className="flex gap-4 items-start">
        {/* Drag handle */}
        <button
          type="button"
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing mt-2 touch-none"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-5 h-5 text-admin-slate-500 hover:text-admin-slate-700" />
        </button>

        {/* Step number */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-admin-accent-blue flex items-center justify-center text-white font-medium mt-2">
          {index + 1}
        </div>

        {/* Step inputs */}
        <div className="flex-1 space-y-3">
          <div>
            <Label htmlFor={`step-${index}-text`}>Step {index + 1} *</Label>
            <Input
              id={`step-${index}-text`}
              value={step.text}
              onChange={(e) => onUpdate(index, 'text', e.target.value)}
              placeholder="Describe this step..."
              maxLength={500}
              required
            />
          </div>
          <div>
            <Label htmlFor={`step-${index}-emphasis`}>
              Emphasis (optional)
            </Label>
            <Input
              id={`step-${index}-emphasis`}
              value={step.emphasis}
              onChange={(e) => onUpdate(index, 'emphasis', e.target.value)}
              placeholder="Key point to emphasize..."
              maxLength={100}
            />
          </div>
        </div>

        {/* Remove button */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          disabled={!canRemove}
          className="mt-2"
          aria-label="Remove step"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
